import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PageView from '../common/pageView';
import globalStyles from '../globals.module.css';
import componentStyles from './checkoutResult.module.css';
import SessionService from '../../service/sessionService';
import { refreshSubscriptionState } from '../../service/billingService';

const POLL_INTERVAL_MS = 2500;
const MAX_POLL_ATTEMPTS = 8;

const CheckoutResult = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const intervalRef = useRef(null);
    const attemptsRef = useRef(0);
    const [viewState, setViewState] = useState({
        kind: 'loading',
        title: 'Checking subscription status',
        message: 'We are refreshing your subscription from the backend.'
    });

    const searchParams = new URLSearchParams(location.search);
    const statusParam = (searchParams.get('status') || '').toLowerCase();
    const isAuthenticated = SessionService.getInstance().isAuthenticated();

    const stopPolling = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    };

    const pollSubscription = useCallback(async () => {
        if (!isAuthenticated) {
            stopPolling();
            setViewState({
                kind: 'login',
                title: 'Sign in to confirm subscription',
                message: 'Checkout may have completed, but this page can only verify backend subscription state after you sign in again.'
            });
            return;
        }

        try {
            const subscriptionState = await refreshSubscriptionState();
            const status = subscriptionState?.subscription?.status || 'inactive';

            if (subscriptionState?.isPaid) {
                stopPolling();
                setViewState({
                    kind: 'confirmed',
                    title: 'Subscription active',
                    message: `Your backend subscription is now ${status}. Pro access is enabled.`
                });
                return;
            }

            attemptsRef.current += 1;
            if (attemptsRef.current >= MAX_POLL_ATTEMPTS) {
                stopPolling();
                setViewState({
                    kind: 'pending',
                    title: 'Processing subscription',
                    message: 'Stripe returned successfully, but the backend subscription has not updated yet. Check your account page again shortly.'
                });
                return;
            }

            setViewState({
                kind: 'processing',
                title: 'Processing subscription',
                message: `Backend subscription status is currently ${status}. Retrying briefly before showing final state.`
            });
        } catch (error) {
            stopPolling();
            setViewState({
                kind: 'error',
                title: 'Unable to verify subscription',
                message: 'We could not load subscription state from the backend right now.'
            });
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (statusParam === 'canceled' || statusParam === 'cancelled') {
            setViewState({
                kind: 'canceled',
                title: 'Checkout canceled',
                message: 'No subscription changes were confirmed. Your account stays on backend subscription truth until a completed checkout is fulfilled.'
            });
            return undefined;
        }

        pollSubscription();
        intervalRef.current = setInterval(pollSubscription, POLL_INTERVAL_MS);

        return () => stopPolling();
    }, [pollSubscription, statusParam]);

    const showSpinner = viewState.kind === 'loading' || viewState.kind === 'processing';

    return (
        <PageView
            title="Billing Status"
            containerClassName={componentStyles.checkoutContainer}
            contentClassName={componentStyles.checkoutContent}
        >
            <section className={`${globalStyles.standardSection} ${componentStyles.statusCard}`}>
                <h3 className={globalStyles.sectionHeader}>{viewState.title}</h3>
                {showSpinner && <div className={globalStyles.spinner}></div>}
                <p className={componentStyles.statusMessage}>{viewState.message}</p>
                <div className={componentStyles.buttonRow}>
                    <button type="button" className={globalStyles.primaryButton} onClick={() => navigate('/home')}>
                        Go To Home
                    </button>
                    {viewState.kind !== 'canceled' && (
                        <button
                            type="button"
                            className={globalStyles.secondaryButton}
                            onClick={() => {
                                stopPolling();
                                attemptsRef.current = 0;
                                setViewState({
                                    kind: 'loading',
                                    title: 'Checking subscription status',
                                    message: 'We are refreshing your subscription from the backend.'
                                });
                                pollSubscription();
                                intervalRef.current = setInterval(pollSubscription, POLL_INTERVAL_MS);
                            }}
                        >
                            Check Again
                        </button>
                    )}
                </div>
            </section>
        </PageView>
    );
};

export default CheckoutResult;
